import React, { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';
import { playSessionCompleteChime, vibrateChime } from '@/utils/sound';
import CTAButton from '@/components/ui/CTAButton';
import styles from './SessionComplete.module.css';

// Max filled dots to show in the streak row. Beyond this we show
// "+N more" rather than a visually noisy grid. 7 chosen to read as
// "a week of consecutive days".
const MAX_STREAK_DOTS = 7;

// Inline rose-stroke line icons used by the achievement context cards
// under the streak dots. 20×20, strokeWidth 1.6, per the design-system pass.
const ICON_SVG = {
  width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
  style: { flexShrink: 0, color: 'var(--primary)' },
};
const ICON_STROKE = {
  stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
};

function CheckIcon() {
  return (
    <svg {...ICON_SVG}>
      <circle cx="12" cy="12" r="9" {...ICON_STROKE} />
      <path d="M8 12.5l2.5 2.5L16 9.5" {...ICON_STROKE} />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg {...ICON_SVG}>
      <path d="M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.5-2.5 1.2-3.2C10 10 10.5 11 11 11c0-2 .5-5 1-8z" {...ICON_STROKE} />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg {...ICON_SVG}>
      <rect x="4" y="5" width="16" height="15" rx="2" {...ICON_STROKE} />
      <path d="M4 10h16M8 3v4M16 3v4" {...ICON_STROKE} />
    </svg>
  );
}

// A small bullet card: white bg, soft border, icon+text row. Used for
// the achievement-context stack under the streak dots. Icon sits in a
// 32px primary-pale disc so the row scans as a rose-dotted checklist
// consistent with the WeekHome bullet-card pattern.
function ContextCard({ icon, text, className, style }) {
  return (
    <div
      className={className}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '14px 16px',
        width: '100%',
        boxShadow: '0 1px 6px rgba(45, 26, 31, 0.04)',
        ...style,
      }}
    >
      <span style={{
        width: 32, height: 32, borderRadius: 16,
        background: 'var(--primary-pale)',
        boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary) 10%, transparent)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </span>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 15,
        color: 'var(--dark-brown)', lineHeight: 1.35,
        fontWeight: 500,
      }}>
        {text}
      </div>
    </div>
  );
}

function StreakDots({ streak, lang = 'en' }) {
  const safe = Math.max(0, streak || 0);
  const visible = Math.min(safe, MAX_STREAK_DOTS);
  const overflow = safe - visible;

  const dots = [];
  for (let i = 0; i < MAX_STREAK_DOTS; i++) {
    let cls = styles.streakDot;
    if (i < visible - 1) cls += ` ${styles['streakDot--lit']}`;
    else if (i === visible - 1) cls += ` ${styles['streakDot--current']}`;
    dots.push(<span key={i} className={cls} />);
  }

  return (
    <div className={styles.streak} aria-label={t('streak_aria', lang).replace('{n}', safe)}>
      <span className={styles.streakDots}>{dots}</span>
      {overflow > 0 && (
        <span className={styles.streakPlus}>+{overflow}</span>
      )}
    </div>
  );
}

export default function SessionComplete() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useApp();

  const {
    newStreak, saveError, session,
    isPersonalBestStreak, sessionsMilestone,
    exerciseCount, sessionMinutes,
  } = location.state ?? {};
  const streak = newStreak || 0;

  // Personal-best line — quiet italic acknowledgement when the
  // user broke their all-time streak record OR hit a round-number
  // sessions milestone. Streak-record beats sessions-milestone if
  // both fire on the same session (rarer signal, more emotional
  // weight). Sits alongside the tomorrow's-preview teaser; both
  // are bridge content between the streak chip and the CTA.
  const personalBestText = useMemo(() => {
    if (isPersonalBestStreak) {
      const tpl = t('complete_personal_best_streak', lang);
      return tpl.replace('{n}', streak);
    }
    if (sessionsMilestone) {
      const tpl = t('complete_personal_best_sessions', lang);
      return tpl.replace('{n}', sessionsMilestone);
    }
    return null;
  }, [isPersonalBestStreak, sessionsMilestone, streak, lang]);

  // Tomorrow's-preview teaser. Maps the session that just finished
  // to a one-line poetic teaser for the next practice. Keeping the
  // mapping in JS (not strings.js) so it stays a single rotation
  // table that's easy to scan and tweak. Falls back to a neutral
  // line when the session type is missing or unrecognised.
  const tomorrowKey = useMemo(() => {
    const type = session?.type;
    switch (type) {
      case 'ENDURANCE':   return 'tomorrow_after_endurance';
      case 'POWER':       return 'tomorrow_after_power';
      case 'RELEASE':     return 'tomorrow_after_release';
      case 'DIRECTION':   return 'tomorrow_after_direction';
      case 'INTEGRATION': return 'tomorrow_after_integration';
      case 'GENTLE':      return 'tomorrow_after_gentle';
      default:            return 'tomorrow_default';
    }
  }, [session?.type]);
  const tomorrowText = t(tomorrowKey, lang);

  const titleKey = useMemo(() => {
    if (streak === 1)  return 'complete_title_day1';
    if (streak <= 6)   return 'complete_title_streak_low';
    if (streak <= 29)  return 'complete_title_streak_mid';
    return 'complete_title_streak_high';
  }, [streak]);

  const titleText = t(titleKey, lang).replace('{n}', streak || 1);

  // Audio + haptic confirmation on arrival. Both are no-ops when
  // their respective gates are off (sound_enabled / haptic_enabled,
  // mirrored from userProgress by AppContext), so callers never need
  // their own guard. Empty dep array: play once per mount.
  useEffect(() => {
    playSessionCompleteChime();
    vibrateChime();
  }, []);

  return (
    <div className={styles.screen}>
      <span aria-hidden="true" className={styles.halo} />
      <div className={styles.rule} aria-hidden="true" />

      <div className={styles.body}>
        <div className={`${styles.bloom} motion-disc-breath`} aria-hidden="true">
          <span className={styles.bloomRing} />
          <span className={`${styles.bloomRing} ${styles['bloomRing--mid']}`} />
          <span className={`${styles.bloomRing} ${styles['bloomRing--inner']}`} />
          <span className={styles.bloomCheck}>
            <svg viewBox="0 0 56 56" fill="none">
              <path
                d="M14 28 L24 38 L42 18"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        <div className={styles.eyebrow}>
          {lang === 'pt' ? 'VOCÊ TERMINOU' : 'YOU FINISHED'}
        </div>
        <h1 className={styles.title}>{titleText}</h1>

        {streak >= 2 && <StreakDots streak={streak} lang={lang} />}

        {/* Achievement context — bullet cards fill the empty space that
            used to sit between the streak dots and the CTA. Icon column
            + short text. Streak card only renders when streak >= 2. */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10,
          width: '100%', maxWidth: 360,
          margin: '24px auto 0',
        }}>
          <ContextCard
            icon={<CheckIcon />}
            text={lang === 'pt' ? 'Sessão concluída' : 'Session complete'}
            className="stagger-item"
            style={{ '--stagger-i': 0 }}
          />
          {streak >= 2 && (
            <ContextCard
              icon={<FlameIcon />}
              text={
                lang === 'pt'
                  ? `Sequência de ${streak} dias`
                  : `${streak}-day streak`
              }
              className="stagger-item"
              style={{ '--stagger-i': 1 }}
            />
          )}
          <ContextCard
            icon={<CalendarIcon />}
            text={lang === 'pt' ? 'Até amanhã' : 'See you tomorrow'}
            className="stagger-item"
            style={{ '--stagger-i': streak >= 2 ? 2 : 1 }}
          />
        </div>

        {saveError && (
          <p className={styles.saveError}>{t('complete_save_error', lang)}</p>
        )}

        {/* "What just happened" recap — two plain bullets that
            describe what the user actually did, in observable
            terms. Honest counts only (no fabricated breath /
            release claims). Renders only when we have stats
            from SessionScreen; older Year-1 paths may have
            partial state and skip silently. */}
        {(exerciseCount > 0 || sessionMinutes) && (
          <ul
            className="stagger-item"
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '20px auto 0',
              maxWidth: 280,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              lineHeight: 1.4,
              color: 'var(--ink-dim, #6b5560)',
              textAlign: 'center',
              '--stagger-i': 2,
            }}
          >
            {sessionMinutes ? (
              <li>{t('complete_recap_minutes', lang).replace('{n}', sessionMinutes)}</li>
            ) : null}
            {exerciseCount > 0 ? (
              <li>
                {t('complete_recap_exercises', lang).replace('{n}', exerciseCount)}
              </li>
            ) : null}
            {session?.type ? (
              <li style={{ fontStyle: 'italic' }}>
                {t('complete_recap_focus', lang).replace(
                  '{focus}',
                  t('session_type_' + session.type.toLowerCase(), lang).toLowerCase()
                )}
              </li>
            ) : null}
          </ul>
        )}

        {/* Personal-best acknowledgement. Renders only when this
            session broke the all-time streak record OR hit a
            round-number sessions milestone. Quiet italic line, no
            confetti, no badge — Be Bold's voice rewards quietly. */}
        {personalBestText && (
          <p
            className="stagger-item"
            style={{
              margin: '24px auto 0',
              maxWidth: 280,
              fontFamily: 'var(--font-title, Fraunces), serif',
              fontStyle: 'italic',
              fontSize: 14,
              lineHeight: 1.4,
              color: 'var(--primary-dark, #a85c4b)',
              textAlign: 'center',
              textWrap: 'balance',
              '--stagger-i': 3,
            }}
          >
            {personalBestText}
          </p>
        )}

        {/* Tomorrow's-preview teaser. Sits between the context
            cards and the return-home CTA. Italic Fraunces serif,
            low-contrast ink, no border or chrome — meant to read
            as a quiet promise rather than a metric. Anticipation
            of tomorrow is one of the strongest non-gamified
            motivators; this is the cheapest way to land it. */}
        {tomorrowText && (
          <p
            className="stagger-item"
            style={{
              margin: '20px auto 0',
              maxWidth: 280,
              fontFamily: 'var(--font-title, Fraunces), serif',
              fontStyle: 'italic',
              fontSize: 14,
              lineHeight: 1.4,
              color: 'var(--ink-dim, #6b5560)',
              textAlign: 'center',
              textWrap: 'balance',
              opacity: 0.85,
              '--stagger-i': 4,
            }}
          >
            {tomorrowText}
          </p>
        )}

        <div className={styles.ctaWrap}>
          <CTAButton
            label={t('complete_return_cta', lang)}
            onClick={() => navigate('/home', { replace: true })}
          />
        </div>
      </div>

      <div className={`${styles.rule} ${styles['rule--bottom']}`} aria-hidden="true" />
    </div>
  );
}
